import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@shared/hooks/use-toast';
import { Spinner } from '@shared/components/ui/Spinner';
import { useRelocationQuery } from '../../hooks/useRelocationQuery';
import { useRelocationFlow } from '../../hooks/useRelocationFlow';
import type { RelocationLine, RelocationStep, RelocationTotal } from '../../models';
import { ActiveLocationSection, Breadcrumb, MovedProductsPanel, StatusMessage } from '../display';
import { QuantityConfirm, ScannerInput } from '../interaction';

const Loading = () => (
  <div className="flex flex-col items-center justify-center w-full min-h-[calc(100vh-150px)]">
    <Spinner size="md" />
  </div>
);

const ErrorMessage = ({ label, detail }: { label: string; detail: string }) => (
  <div className="container mx-auto p-4 max-w-6xl">
    <div className="text-center py-8">
      <p className="text-red-600 dark:text-red-400">
        {label}: {detail}
      </p>
    </div>
  </div>
);

interface ActionPanelProps {
  isReadOnly: boolean;
  relocationStatus: 'draft' | 'dispatched' | 'cancelled';
  assignedCartLabel?: string;
  assignedLocationOrderId?: number | null;
  dispatchedAt?: string | null;
  currentStep: RelocationStep;
  cartCode?: string;
  selectedLocation?: { id: number; code: string };
  selectedProduct?: { id: number; code?: string; name?: string; image?: string };
  onScan: (code: string) => Promise<{ success: boolean; message: string }>;
  onConfirmQuantity: (qty: number) => Promise<{ success: boolean; message: string }>;
  onCancelQuantity: () => void;
}

const ActionPanel: React.FC<ActionPanelProps> = ({
  isReadOnly,
  relocationStatus,
  assignedCartLabel,
  assignedLocationOrderId,
  dispatchedAt,
  currentStep,
  cartCode,
  selectedLocation,
  selectedProduct,
  onScan,
  onConfirmQuantity,
  onCancelQuantity,
}) => {
  if (isReadOnly) {
    return (
      <StatusMessage
        status={relocationStatus}
        assignedCartLabel={assignedCartLabel}
        assignedLocationOrderId={assignedLocationOrderId}
        dispatchedAt={dispatchedAt}
      />
    );
  }

  if (currentStep === 'quantity' && selectedProduct && selectedLocation) {
    return (
      <QuantityConfirm
        selectedProduct={selectedProduct}
        selectedLocation={selectedLocation}
        onConfirm={onConfirmQuantity}
        onCancel={onCancelQuantity}
      />
    );
  }

  return <ScannerInput currentStep={currentStep} onScan={onScan} cartCode={cartCode} />;
};

export const Main: React.FC = () => {
  const { t } = useTranslation('relocationProcessModule');
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const isNew = !id || id === 'new';
  const relocationId = isNew ? 0 : Number(id);

  const cameFromCartScan = Boolean((location.state as { fromCartScan?: boolean } | null)?.fromCartScan);

  const { relocation, loading, error, refetch, appendLine, updateRelocation } = useRelocationQuery(relocationId);

  const handleRelocationCreated = (newId: number) => {
    navigate(`/relocations/${newId}`, { replace: true, state: { fromCartScan: true } });
  };

  const handleLineAdded = (line: RelocationLine) => {
    appendLine(line);
    // Sincroniza con backend para reemplazar línea optimista (caso 204 No Content)
    refetch();
  };

  const handleDispatched = () => {
    if (relocation) {
      updateRelocation({ ...relocation, status: 'dispatched', dispatched_at: new Date().toISOString() });
    }
    refetch();
  };

  const {
    cart,
    selectedLocation,
    selectedProduct,
    currentStep,
    isDispatching,
    processScannedCode,
    confirmQuantity,
    goBackToLocationStep,
    dispatchRelocation,
  } = useRelocationFlow({
    relocation,
    onRelocationCreated: handleRelocationCreated,
    onLineAdded: handleLineAdded,
    onDispatched: handleDispatched,
  });

  // Errores / loading
  if (!isNew && loading && !cameFromCartScan) {
    return <Loading />;
  }

  if (!isNew && error) {
    return <ErrorMessage label={t('error')} detail={error} />;
  }

  if (!isNew && !loading && !relocation) {
    return <ErrorMessage label={t('error')} detail={t('errors.not_found')} />;
  }

  const status = relocation?.status ?? 'draft';
  const isReadOnly = status === 'dispatched' || status === 'cancelled';
  const lines: RelocationLine[] = relocation?.lines || [];
  const totals: RelocationTotal[] = relocation?.totals || [];
  const canDispatch = !isReadOnly && !!relocation && lines.length > 0;
  const assignedCartLabel =
    cart?.code || relocation?.cart?.code || (relocation?.cart_id ? `#${relocation.cart_id}` : undefined);

  const onConfirmQuantity = async (qty: number) => {
    const res = await confirmQuantity(qty);
    toast({
      title: res.success ? t('toasts.success') : t('toasts.error'),
      description: res.message,
      variant: res.success ? 'default' : 'destructive',
    });
    return res;
  };

  const onDispatch = async () => {
    const res = await dispatchRelocation();
    toast({
      title: res.success ? t('toasts.success') : t('toasts.error'),
      description: res.message,
      variant: res.success ? 'default' : 'destructive',
    });
  };

  return (
    <>
      <Breadcrumb
        relocation={relocation}
        isNew={isNew}
        canDispatch={canDispatch}
        isDispatching={isDispatching}
        onDispatch={onDispatch}
      />

      <div className="container mx-auto p-4 max-w-6xl h-[calc(100vh-6rem)] flex flex-col">
        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Left panel: scanner / quantity / status */}
          <div className="lg:flex-1 lg:pr-8 flex-shrink-0 lg:flex lg:flex-col min-h-0">
            <div className={`mb-4 min-h-0 ${isReadOnly ? 'flex-1 flex flex-col' : 'flex-shrink-0'}`}>
              <ActionPanel
                isReadOnly={isReadOnly}
                relocationStatus={status}
                assignedCartLabel={assignedCartLabel}
                assignedLocationOrderId={relocation?.location_order_id ?? null}
                dispatchedAt={relocation?.dispatched_at ?? null}
                currentStep={currentStep}
                cartCode={cart?.code}
                selectedLocation={selectedLocation}
                selectedProduct={selectedProduct}
                onScan={processScannedCode}
                onConfirmQuantity={onConfirmQuantity}
                onCancelQuantity={goBackToLocationStep}
              />
            </div>

            {!isReadOnly && currentStep === 'product' && selectedLocation && (
              <div className="flex-shrink-0 mb-4">
                <ActiveLocationSection location={selectedLocation} onGoBack={goBackToLocationStep} />
              </div>
            )}
          </div>

          {/* Vertical separator - desktop only */}
          <div className="hidden lg:block w-px bg-border flex-shrink-0" />

          <MovedProductsPanel lines={lines} totals={totals} cartLabel={assignedCartLabel} />
        </div>
      </div>
    </>
  );
};

export default Main;
